import React, { useEffect, useCallback } from 'react'
import isNumber from 'lodash/isNumber'
import upperCase from 'lodash/upperCase'
import { PermissionId, RouteRoleLevel } from 'shared/types'
import { Redirect } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { authRequest, User } from 'shared/redux/auth/reducer'
import { GlobalState } from 'shared/redux/reducer'
import ForbiddenPage from 'shared/components/ForbiddenPage'
import Loader from 'shared/components/Loader'
import {
  CAN_CREATE_COACH_PROFILES,
  CAN_EXPORT_COACH_PROFILES,
  CAN_VIEW_COACH_LIST,
  CAN_CREATE_PARTICIPANT_PROFILES,
  CAN_SIGN_WAIVER_FOR_PARTICIPANT,
  CAN_EXPORT_PARTICIPANT_PROFILES,
  CAN_VIEW_PARTICIPANT_LIST,
  CAN_CHECK_IN_OUT_MEMBERS,
  CAN_EXPORT_CHECK_IN_OUT_MEMBERS,
  CAN_VIEW_CHECK_IN_OUT_MEMBERS,
  CAN_CREATE_PROGRAMS,
  CAN_CREATE_EVENTS,
  IS_VIEW_ASSIGNED_ONLY,
  CAN_ADD_IN_DROP_IN_PARTICIPANTS,
  CAN_CREATE_POINTS,
  CAN_REWARD_CHALLENGE_POINTS,
  CAN_REWARD_LIFE_SKILLS_POINTS,
  CAN_ADD_ITEMS_TO_STORE,
  CAN_CREATE_SURVEY,
  CAN_EXPORT_SURVEY_RESULTS,
  CAN_VIEW_REPORTS,
  CAN_CREATE_COMMUNICATIONS,
  CAN_VIEW_SCHEDULE,
  CAN_VIEW_PROGRAMS,
  CAN_VIEW_EVENTS,
  CAN_VIEW_INBOX,
  CAN_VIEW_CHALLENGES,
  CAN_VIEW_STORE,
  CAN_VIEW_LEADERBOARD,
  CAN_TAKE_ATTENDANCE,
  CAN_EXPORT_EVENTS,
  CAN_EXPORT_PROGRAMS,
  CAN_MANAGE_PARENT,
} from 'shared/constants/permission'
import {
  organizationInformationType,
  organizationInformationFeaturesType,
} from 'pages/CustomizeScoreboard/model/organizationInformation'

export const KIOSK = 7
export const KIOSK_TEXT = 'KIOSK'
export const PARTICIPANT = 1
export const PARTICIPANT_ROLE_TEXT = 'PARTICIPANT'
export const PARENT = 1
export const PARENT_ROLE_TEXT = 'PARENT'
export const COACH = 2
export const COACH_ROLE_TEXT = 'COACH'
export const ADMIN = 3
export const ADMIN_ROLE_TEXT = 'ADMIN'
export const ORGANIZATION = 4
export const ORGANIZATION_ROLE_TEXT = 'ORGANIZATION'
export const GRANTER = 5
export const GRANTER_ROLE_TEXT = 'GRANTER'
export const SUPER_ADMIN = 6
export const SUPER_ADMIN_ROLE_TEXT = 'SUPERADMIN'

export const USER_ROLES = ['Admin', 'Coach', 'Participant', 'Organization', 'Granter', 'Superadmin', 'Parent']

export const roleSelector = createSelector(
  (state: GlobalState) => state.auth.user,
  auth => ({
    isAdmin: auth?.role_level === ADMIN,
    isKiosk: auth?.role_level === KIOSK,
    isCoach: auth?.role_level === COACH && upperCase(auth?.role_name) === COACH_ROLE_TEXT,
    isParent: auth?.role_level === PARENT && upperCase(auth?.role_name) === PARENT_ROLE_TEXT,
    isParticipant: auth?.role_level === PARTICIPANT && upperCase(auth?.role_name) === PARTICIPANT_ROLE_TEXT,
    isOrganization: auth?.role_level === ORGANIZATION && upperCase(auth?.role_name) === ORGANIZATION_ROLE_TEXT,
    isGranter: auth?.role_level === GRANTER && upperCase(auth?.role_name) === GRANTER_ROLE_TEXT,
    isSuperAdmin: auth?.role_level === SUPER_ADMIN && upperCase(auth?.role_name) === SUPER_ADMIN_ROLE_TEXT,
  }),
)

export const authSelector = createSelector(
  (state: GlobalState) => state.auth.user,
  state => state.auth.sessionLoading,
  state => state.auth.sessionRequested,
  state => state.auth.registrationDialog,
  state => state.auth.registrationBanner,
  (auth, sessionLoading, sessionRequested, registrationDialog, registrationBanner) => ({
    auth,
    sessionLoading,
    sessionRequested,
    registrationDialog,
    registrationBanner,
  }),
)

export const userSessionSelector = createSelector(
  (state: GlobalState) => state.user_session.children_event_enrollment,
  state => state.user_session.children_program_enrollment,
  state => state.user_session.num_of_programs_enrolled,
  state => state.user_session.program_enrollment,
  state => state.user_session.event_enrollment,
  (
    children_event_enrollment,
    children_program_enrollment,
    num_of_programs_enrolled,
    program_enrollment,
    event_enrollment,
  ) => ({
    children_event_enrollment,
    children_program_enrollment,
    num_of_programs_enrolled,
    program_enrollment,
    event_enrollment,
  }),
)

export const organizationSettingSelector = createSelector(
  (state: GlobalState) => state.organizationSetting.organization_information,
  organization_information => ({
    organization_information,
  }),
)

export const screeningSelector = createSelector(
  (state: GlobalState) => state.app.isScreeningRequested,
  (state: GlobalState) => state.app.screening,
  (isScreeningRequested, screening) => ({
    isScreeningRequested,
    screening,
  }),
)

export const permissionSelector = createSelector(
  (state: GlobalState) => state.auth.user,
  user => {
    const permission_ids = user?.permission_ids || []
    return {
      CAN_CREATE_COACH_PROFILES: permission_ids.includes(CAN_CREATE_COACH_PROFILES),
      CAN_EXPORT_COACH_PROFILES: permission_ids.includes(CAN_EXPORT_COACH_PROFILES),
      CAN_VIEW_COACH_LIST: permission_ids.includes(CAN_VIEW_COACH_LIST),
      CAN_CREATE_PARTICIPANT_PROFILES: permission_ids.includes(CAN_CREATE_PARTICIPANT_PROFILES),
      CAN_SIGN_WAIVER_FOR_PARTICIPANT: permission_ids.includes(CAN_SIGN_WAIVER_FOR_PARTICIPANT),
      CAN_EXPORT_PARTICIPANT_PROFILES: permission_ids.includes(CAN_EXPORT_PARTICIPANT_PROFILES),
      CAN_VIEW_PARTICIPANT_LIST: permission_ids.includes(CAN_VIEW_PARTICIPANT_LIST),
      CAN_CHECK_IN_OUT_MEMBERS: permission_ids.includes(CAN_CHECK_IN_OUT_MEMBERS),
      CAN_VIEW_CHECK_IN_OUT_MEMBERS: permission_ids.includes(CAN_VIEW_CHECK_IN_OUT_MEMBERS),
      CAN_EXPORT_CHECK_IN_OUT_MEMBERS: permission_ids.includes(CAN_EXPORT_CHECK_IN_OUT_MEMBERS),
      CAN_CREATE_PROGRAMS: permission_ids.includes(CAN_CREATE_PROGRAMS),
      CAN_CREATE_EVENTS: permission_ids.includes(CAN_CREATE_EVENTS),
      IS_VIEW_ASSIGNED_ONLY: permission_ids.includes(IS_VIEW_ASSIGNED_ONLY),
      CAN_ADD_IN_DROP_IN_PARTICIPANTS: permission_ids.includes(CAN_ADD_IN_DROP_IN_PARTICIPANTS),
      CAN_CREATE_POINTS: permission_ids.includes(CAN_CREATE_POINTS),
      CAN_REWARD_CHALLENGE_POINTS: permission_ids.includes(CAN_REWARD_CHALLENGE_POINTS),
      CAN_REWARD_LIFE_SKILLS_POINTS: permission_ids.includes(CAN_REWARD_LIFE_SKILLS_POINTS),
      CAN_ADD_ITEMS_TO_STORE: permission_ids.includes(CAN_ADD_ITEMS_TO_STORE),
      CAN_CREATE_SURVEY: permission_ids.includes(CAN_CREATE_SURVEY),
      CAN_EXPORT_SURVEY_RESULTS: permission_ids.includes(CAN_EXPORT_SURVEY_RESULTS),
      CAN_VIEW_REPORTS: permission_ids.includes(CAN_VIEW_REPORTS),
      CAN_CREATE_COMMUNICATIONS: permission_ids.includes(CAN_CREATE_COMMUNICATIONS),
      CAN_VIEW_SCHEDULE: permission_ids.includes(CAN_VIEW_SCHEDULE),
      CAN_VIEW_PROGRAMS: permission_ids.includes(CAN_VIEW_PROGRAMS),
      CAN_VIEW_EVENTS: permission_ids.includes(CAN_VIEW_EVENTS),
      CAN_VIEW_INBOX: permission_ids.includes(CAN_VIEW_INBOX),
      CAN_VIEW_CHALLENGES: permission_ids.includes(CAN_VIEW_CHALLENGES),
      CAN_VIEW_STORE: permission_ids.includes(CAN_VIEW_STORE),
      CAN_VIEW_LEADERBOARD: permission_ids.includes(CAN_VIEW_LEADERBOARD),
      CAN_TAKE_ATTENDANCE: permission_ids.includes(CAN_TAKE_ATTENDANCE),
      CAN_EXPORT_EVENTS: permission_ids.includes(CAN_EXPORT_EVENTS),
      CAN_EXPORT_PROGRAMS: permission_ids.includes(CAN_EXPORT_PROGRAMS),
      CAN_MANAGE_PARENT: permission_ids.includes(CAN_MANAGE_PARENT),
    }
  },
)

export function isForbidden(
  user: null | User,
  organization_information: organizationInformationType,
  level: RouteRoleLevel,
  permissions: PermissionId[] = [],
  features: string[] = [],
  isKioskAllowed = false,
) {
  const EQUAL = Array.isArray(level?.equal) ? level?.equal : [level?.equal || 0]
  const IS_GREATER = user && user?.role_level > (level?.up || 0)
  const IS_LOWER = user && user?.role_level < (level?.down || 0)
  const IS_EQUAL = user && EQUAL?.includes(user?.role_level)
  const PERMITTED = permissions.some((permission: string) => (user?.permission_ids || []).includes(permission))
  const org_features = organization_information.features as organizationInformationFeaturesType
  const FEATURED = features.some((feature: string) => {
    if (org_features[feature] && org_features[feature].is_enabled) return true
  })

  if (user && user?.role_level == KIOSK) {
    if (!isKioskAllowed) {
      return true
    } else if (permissions.length && !PERMITTED) {
      return true
    } else {
      return false
    }
  }

  if (isNumber(level?.up) && !(IS_GREATER || IS_EQUAL)) {
    return true
  }

  if (isNumber(level?.down) && !(IS_LOWER || IS_EQUAL)) {
    return true
  }

  if (!isNumber(level?.up) && !isNumber(level?.down) && isNumber(level?.equal) && !IS_EQUAL) {
    return true
  }

  if (user && permissions.length && !PERMITTED) {
    return true
  }

  if (organization_information && features.length && !FEATURED) {
    return true
  }

  return false
}

const withAuth = (WrappedComponent: any) => {
  function Auth(props: any) {
    const dispatch = useDispatch()
    const { requireAuth, permissions = [], features = [], level = {}, isKioskAllowed = false } = props
    const { auth, sessionLoading, sessionRequested } = useSelector(authSelector)
    const { organization_information } = useSelector(organizationSettingSelector)

    const checkSession = useCallback(() => {
      if (!sessionRequested) {
        dispatch(authRequest())
      }
    }, [sessionRequested])

    useEffect(checkSession, [])

    if ((!sessionRequested && !auth) || (sessionLoading && requireAuth !== 'optional')) {
      return <Loader />
    }
    if (!auth && sessionRequested && requireAuth === true) {
      return <Redirect to={`/login`} />
    }
    if (auth && requireAuth === false) {
      return <Redirect to={`/`} />
    }
    if (isForbidden(auth, organization_information, level, permissions, features, isKioskAllowed)) {
      return <ForbiddenPage />
    }
    return <WrappedComponent {...props} />
  }

  return Auth
}

export default withAuth
